import { useState, useEffect, useRef } from 'react'

import { useReactiveVar } from '@apollo/client'
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk'
import { snakeCase } from 'lodash'
import getConfig from 'next/config'

import PageLoader from '~/components/ui/PageLoader'
import { getEventDeviceId } from '~/events/utils/eventDeviceId'
import { currentUserVar } from '~/graphql/reactive-vars'
import useLocale from '~/hooks/useLocale'
import { useUserAgentInfo } from '~/hooks/useUserAgentInfo'
import logger from '~/utils/logger'

const { publicRuntimeConfig } = getConfig()
const buildTimestamp = publicRuntimeConfig?.buildTimestamp ?? 0
const isLDEnabled = publicRuntimeConfig?.launchDarkly?.enabled
const ldClientSideID = publicRuntimeConfig?.launchDarkly?.clientSideID
const isLDAvailable = isLDEnabled && ldClientSideID
type LDProvider = ({ children }: { children: JSX.Element | JSX.Element[] }) => JSX.Element

export const FeaturesProvider = ({ children }: { children: JSX.Element | JSX.Element[] }) => {
  const { uaResults } = useUserAgentInfo()
  const { locale } = useLocale()
  const currentUser = useReactiveVar(currentUserVar)
  const userId = currentUser?.id
  const providerRef = useRef<LDProvider | null>(null)
  const [isReady, setIsReady] = useState(false) // we want component to rerender when its ready

  useEffect(() => {
    if (!isLDAvailable) {
      logger.debug('[LaunchDarkly] - Offline mode')
      setIsReady(true)
      return
    }

    if (!userId) {
      // https://launchdarkly.com/docs/home/observability/anonymous-contexts#wait-to-initialize-your-sdk
      // if user is not there, we don't need to initialize LD
      // as of now we don't have anonymous user feature flag to be evaluated.
      // helps in reducing MAU count
      setIsReady(true)
      return
    }

    ;(async () => {
      const component = await asyncWithLDProvider({
        clientSideID: ldClientSideID,
        context: {
          kind: 'multi',
          user: {
            key: String(userId),
          },
          device: {
            key: getEventDeviceId(),
            // below are the properties using which we can control feature flags
            clientName: 'web', // web | mobile (send via both)
            locale: locale,
            // appVersion: '10.0.1', // mobile app version (only mobile)
            // appBuildNumber: 1232, // number : mobile app build number (only mobile)
            // appPlatform: '', // android | ios | browser (only mobile)
            // appManufacturer: '', // (only mobile)
            // appRegion: '', // (only mobile)
            webBuildTime: buildTimestamp, // number : timestamp of build (only web)
            webDeviceType: snakeCase(uaResults.device.type || 'desktop'), // desktop | mobile ( is mweb) | smarttv (only web)
            browserName: snakeCase(uaResults.browser.name), // browser name (only web)
          },
        },
        options: {
          streaming: true, // TODO - check if we need to manually enable streaming, or is default is already true with new sdk version
          logger: {
            debug: (...args) => logger.debug('[LaunchDarkly]', ...args),
            info: (...args) => logger.info('[LaunchDarkly]', ...args),
            warn: (...args) => logger.warn('[LaunchDarkly]', ...args),
            error: (...args) => logger.error('[LaunchDarkly]', ...args),
          },
        },
        reactOptions: {
          useCamelCaseFlagKeys: false,
        },
      })
      providerRef.current = component
    })()
      .catch((e) => {
        logger.error('FeaturesProvider::Error', e)
        providerRef.current = null // resetting to null, as error is occurred
      })
      .finally(() => {
        setIsReady(true)
      })
  }, [locale, uaResults.browser.name, uaResults.device.type, userId])

  if (!isReady) {
    return <PageLoader />
  }

  const Provider = providerRef.current

  if (isLDAvailable && Provider) {
    return <Provider>{children}</Provider>
  } else {
    return <>{children}</>
  }
}
